import React from "react";
import { Phone, Mail, MapPin, MessageCircle, Navigation, Clock } from "lucide-react";
import {
  CLINIC_PHONE_DISPLAY,
  CLINIC_PHONE_TEL,
  CLINIC_EMAIL,
  buildWhatsAppGenericUrl,
} from "../lib/whatsapp";

export const Contact = () => (
  <section
    id="contact"
    data-testid="contact-section"
    className="py-20 sm:py-28 lg:py-32 bg-white"
  >
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <p className="text-xs sm:text-sm text-brand-600 font-semibold tracking-[0.18em] uppercase">
            Get in Touch
          </p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900">
            Let's plan your <em className="not-italic bg-gradient-to-r from-brand-400 to-accent-400 bg-clip-text text-transparent">recovery</em>.
          </h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            Reach out on WhatsApp or call directly — we usually respond within
            a few minutes.
          </p>

          <div className="mt-8 space-y-4">
            <ContactRow
              icon={Phone}
              label="Phone"
              value={CLINIC_PHONE_DISPLAY}
              href={`tel:${CLINIC_PHONE_TEL}`}
              testid="contact-phone"
            />
            <ContactRow
              icon={Mail}
              label="Email"
              value={CLINIC_EMAIL}
              href={`mailto:${CLINIC_EMAIL}`}
              testid="contact-email"
            />
            <ContactRow
              icon={MapPin}
              label="Service Area"
              value="Home visits across the city"
              testid="contact-area"
            />
            <ContactRow
              icon={Clock}
              label="Clinic Timings"
              value="Mon - Sat: 8 AM to 9 PM · Sunday: Closed"
              testid="contact-timings"
            />
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            <a
              href={buildWhatsAppGenericUrl()}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full h-12 px-6 text-base font-semibold bg-[#25D366] hover:bg-[#1ebe57] text-white shadow-md shadow-brand-500/20 transition-all hover:-translate-y-0.5"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp Us
            </a>
            <a
              href={`tel:${CLINIC_PHONE_TEL}`}
              data-testid="contact-call-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full h-12 px-6 text-base font-semibold border border-slate-200 bg-white text-slate-800 hover:bg-slate-50 transition-all hover:-translate-y-0.5"
            >
              <Phone className="w-4 h-4 text-brand-600" />
              Call Now
            </a>
          </div>
        </div>

        {/* Map placeholder */}
        <div
          className="lg:col-span-7 relative rounded-3xl overflow-hidden border border-slate-100 shadow-[0_20px_60px_rgba(44,62,80,0.08)] aspect-[5/4] sm:aspect-[6/4] bg-gradient-to-br from-brand-50 via-white to-accent-50"
          data-testid="contact-map-placeholder"
        >
          {/* Decorative grid */}
          <svg
            className="absolute inset-0 w-full h-full text-brand-200/50"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path
                  d="M 40 0 L 0 0 0 40"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.5"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>

          {/* "Roads" */}
          <div className="absolute inset-0">
            <div className="absolute top-1/3 left-0 right-0 h-1.5 bg-white/70 rounded-full" />
            <div className="absolute top-2/3 left-0 right-0 h-1 bg-white/70 rounded-full" />
            <div className="absolute top-0 bottom-0 left-1/3 w-1.5 bg-white/70 rounded-full" />
            <div className="absolute top-0 bottom-0 right-1/4 w-1 bg-white/70 rounded-full" />
          </div>

          {/* Pin */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-brand-500/30 blur-2xl scale-150" />
              <div className="relative w-16 h-16 rounded-full bg-brand-500 flex items-center justify-center shadow-xl shadow-brand-500/40 animate-bounce">
                <MapPin className="w-8 h-8 text-white" strokeWidth={2} />
              </div>
            </div>
          </div>

          <div className="absolute bottom-5 left-5 right-5 sm:left-6 sm:right-6 bg-white/90 backdrop-blur-xl border border-slate-100 rounded-2xl px-5 py-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center">
                <Navigation className="w-5 h-5 text-brand-600" strokeWidth={1.6} />
              </span>
              <div>
                <p className="text-xs text-slate-500">Home-visit Service Area</p>
                <p className="text-sm font-semibold text-slate-900">
                  Available across major neighbourhoods
                </p>
              </div>
            </div>
            <a
              href={buildWhatsAppGenericUrl("Hi Doctor, do you provide home visits in my area?")}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-semibold text-brand-700 hover:text-brand-800 whitespace-nowrap"
            >
              Check →
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ContactRow = ({ icon: Icon, label, value, href, testid }) => {
  const inner = (
    <div className="flex items-center gap-4 p-4 rounded-2xl border border-slate-200 hover:border-brand-300 hover:bg-brand-50/40 transition-colors group">
      <span className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center">
        <Icon className="w-5 h-5 text-brand-600" strokeWidth={1.5} />
      </span>
      <div className="flex-1">
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-sm font-semibold text-slate-900 group-hover:text-brand-700">
          {value}
        </p>
      </div>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testid}>
      {inner}
    </a>
  ) : (
    <div data-testid={testid}>{inner}</div>
  );
};
